import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ArrowUp, ArrowDown, ArrowUpDown, Download } from 'lucide-react';
import { format } from 'date-fns';
import { getPERates, getPEInventoryMetrics } from '@/services/marketIntelService';
import type { PERatesItem } from '@/types/pe.types';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import useChartWindow from '@/hooks/useChartWindow';
import { parseDateLiteral } from '@/utils/chartAxisUtils';
import { useRsColors } from '@/hooks/useRsColors';
import { createVerticalHoverLinePlugin, createAreaBetweenLinesPlugin } from '@/utils/chartPlugins';
import { useTheme } from '@/contexts/ThemeContext';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler
);

const SHADING = {
  rowEven: { dark: '#141613', light: '#ffffff' },
  rowOdd: { dark: '#1c1e1a', light: '#f5f7f3' },
};

const RESIN_COLORS: Record<string, { border: string; bg: string; header: string }> = {
  pe:    { border: 'rgba(59,130,246,0.9)',  bg: 'rgba(59,130,246,0.15)',  header: 'rgba(59,130,246,0.7)' },
  hdpe:  { border: 'rgba(16,185,129,0.9)',  bg: 'rgba(16,185,129,0.15)',  header: 'rgba(16,185,129,0.7)' },
  ldpe:  { border: 'rgba(245,158,11,0.9)',  bg: 'rgba(245,158,11,0.15)',  header: 'rgba(245,158,11,0.7)' },
  lldpe: { border: 'rgba(236,72,153,0.9)',  bg: 'rgba(236,72,153,0.15)',  header: 'rgba(236,72,153,0.7)' },
};

const RESIN_TYPES = ['hdpe', 'ldpe', 'lldpe', 'pe'] as const;
const RESIN_LABELS: Record<string, string> = {
  pe: 'PE Total',
  hdpe: 'HDPE',
  ldpe: 'LDPE',
  lldpe: 'LLDPE',
};

interface PERatesTabProps {
  dateFrom?: string;
  dateTo?: string;
}

type SortColumn = 'date' | 'demand_rates' | 'operating_rates' | 'export_rates' | 'inventory';
type SortDirection = 'asc' | 'desc';
type SortState = Record<string, { column: SortColumn; direction: SortDirection }>;

const PERatesTab: React.FC<PERatesTabProps> = ({ dateFrom, dateTo }) => {
  const { isDark } = useTheme();
  const [data, setData] = useState<PERatesItem[]>([]);
  const [inventoryByResin, setInventoryByResin] = useState<Record<string, Record<string, number | null>>>({});
  const [loading, setLoading] = useState(true);
  const [focusedDatasetIndex, setFocusedDatasetIndex] = useState<number | null>(null);
  const [sortState, setSortState] = useState<SortState>({
    pe: { column: 'date', direction: 'desc' },
    hdpe: { column: 'date', direction: 'desc' },
    ldpe: { column: 'date', direction: 'desc' },
    lldpe: { column: 'date', direction: 'desc' },
  });
  // Below 750px, let the chart go edge-to-edge.
  const [isCompact, setIsCompact] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    setIsCompact(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsCompact(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [ratesResult, invPE, invHDPE, invLDPE, invLLDPE] = await Promise.all([
          getPERates(dateFrom, dateTo),
          getPEInventoryMetrics('pe', dateFrom, dateTo).catch(() => []),
          getPEInventoryMetrics('hdpe', dateFrom, dateTo).catch(() => []),
          getPEInventoryMetrics('ldpe', dateFrom, dateTo).catch(() => []),
          getPEInventoryMetrics('lldpe', dateFrom, dateTo).catch(() => []),
        ]);
        setData(Array.isArray(ratesResult) ? ratesResult : []);

        const buildMap = (rows: any[]) => {
          const map: Record<string, number | null> = {};
          if (Array.isArray(rows)) {
            rows.forEach((row: any) => {
              map[row.date] = row.days_of_inventory !== null && row.days_of_inventory !== undefined
                ? Number(row.days_of_inventory)
                : null;
            });
          }
          return map;
        };

        setInventoryByResin({
          pe: buildMap(invPE as any[]),
          hdpe: buildMap(invHDPE as any[]),
          ldpe: buildMap(invLDPE as any[]),
          lldpe: buildMap(invLLDPE as any[]),
        });
      } catch (err) {
        console.error('PERatesTab fetch error', err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateFrom, dateTo]);

  const dataByResin = RESIN_TYPES.reduce<Record<string, PERatesItem[]>>((acc, rt) => {
    acc[rt] = data.filter((d) => d.resin_type === rt);
    return acc;
  }, {});

  const peDates = dataByResin['pe'].map((d) => d.date).sort();

  const chartLabels = peDates.map((d) => {
    try { return format(new Date(d + 'T00:00:00'), 'MMM yyyy'); } catch { return d; }
  });

  const buildLineDataset = (resinType: string, metricType: 'operating_rates' | 'demand_rates', datasetIndex: number) => {
    const rows = dataByResin[resinType] || [];
    const dateMap = Object.fromEntries(rows.map((r) => [r.date, r]));
    const metricLabel = metricType === 'operating_rates' ? 'Operating Rate' : 'Demand Rate';
    const data = peDates.map((d) => {
      const val = dateMap[d]?.[metricType];
      return val !== null && val !== undefined ? Number(val) * 100 : null;
    });

    const opacity = focusedDatasetIndex === null || focusedDatasetIndex === datasetIndex ? 1 : 0.2;

    return {
      label: metricLabel,
      data,
      borderColor: metricType === 'operating_rates'
        ? `rgba(0,102,255,${opacity})`
        : `rgba(255,140,0,${opacity})`,
      backgroundColor: 'transparent',
      fill: false,
      pointRadius: 0,
      borderWidth: 4,
      tension: 0.3,
      spanGaps: true,
    };
  };

  const allDatasets = [
    buildLineDataset('pe', 'operating_rates', 0),
    buildLineDataset('pe', 'demand_rates', 1),
  ];

  const defaultDateWindow = useMemo(() => {
    if (chartLabels.length === 0) return { startIndex: 0, endIndex: 0 };
    const endIndex = chartLabels.length - 1;
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - 3);
    cutoff.setMonth(cutoff.getMonth() - 1);
    const firstIdx = chartLabels.findIndex((label) => {
      const d = parseDateLiteral(String(label));
      return !isNaN(d.getTime()) && d.getTime() >= cutoff.getTime();
    });
    return {
      startIndex: firstIdx >= 0 ? firstIdx : 0,
      endIndex,
    };
  }, [chartLabels]);

  const { startIndex, endIndex, setWindow, hasWindowing } = useChartWindow(chartLabels.length, {
    defaultStartIndex: defaultDateWindow.startIndex,
    defaultEndIndex: defaultDateWindow.endIndex,
  });

  const visibleLabels = useMemo(
    () => chartLabels.slice(startIndex, endIndex + 1),
    [chartLabels, startIndex, endIndex],
  );

  const visibleDatasets = useMemo(
    () => allDatasets.map((ds) => ({ ...ds, data: ds.data.slice(startIndex, endIndex + 1) })),
    [allDatasets, startIndex, endIndex],
  );

  const chartData = {
    labels: visibleLabels,
    datasets: visibleDatasets,
  };

  const xTickIndices = useMemo(() => {
    const set = new Set<number>();
    let monthsBack = 0;
    let prevLabel: string | null = null;
    for (let i = visibleLabels.length - 1; i >= 0; i--) {
      const label = visibleLabels[i];
      if (label !== prevLabel) {
        if (monthsBack % 6 === 0) set.add(i);
        monthsBack += 1;
        prevLabel = label;
      }
    }
    return set;
  }, [visibleLabels]);

  const rs = useRsColors();

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs, isDark]);
  const areaPlugin = useMemo(() => createAreaBetweenLinesPlugin(), []);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: { color: rs.offWhite, font: { size: 11 } },
        onClick: (_e: any, legendItem: any) => {
          const index = legendItem.datasetIndex;
          setFocusedDatasetIndex(focusedDatasetIndex === index ? null : index);
        },
      },
      tooltip: { mode: 'index' as const, intersect: false },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: {
          color: rs.offWhite,
          font: { size: 10 },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: function (_value: any, index: number) {
            return xTickIndices.has(index) ? visibleLabels[index] : '';
          },
        },
        grid: { color: rs.gridLine },
      },
      y: {
        ticks: { color: rs.offWhite, callback: (v: any) => `${Math.round(v)}%` },
        grid: { color: rs.gridLine },
      },
    },
  }), [rs, xTickIndices, visibleLabels, focusedDatasetIndex]);

  const formatRate = (v?: number | null) => {
    if (v === null || v === undefined) return '—';
    return `${Math.round(Number(v) * 100)}%`;
  };

  const formatDays = (v?: number | null) => {
    if (v === null || v === undefined) return '—';
    return `${Number(v).toFixed(0)}`;
  };

  const handleColumnSort = (resinType: string, column: SortColumn) => {
    const currentSort = sortState[resinType];
    if (currentSort.column === column) {
      setSortState(prev => ({
        ...prev,
        [resinType]: { column, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' }
      }));
    } else {
      setSortState(prev => ({
        ...prev,
        [resinType]: { column, direction: 'desc' }
      }));
    }
  };

  const getSortedRows = (rows: PERatesItem[], resinType: string) => {
    const sorted = [...rows];
    const { column, direction } = sortState[resinType];

    sorted.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (column) {
        case 'date':
          aVal = a.date;
          bVal = b.date;
          break;
        case 'demand_rates':
          aVal = a.demand_rates !== null ? Number(a.demand_rates) : -Infinity;
          bVal = b.demand_rates !== null ? Number(b.demand_rates) : -Infinity;
          break;
        case 'operating_rates':
          aVal = a.operating_rates !== null ? Number(a.operating_rates) : -Infinity;
          bVal = b.operating_rates !== null ? Number(b.operating_rates) : -Infinity;
          break;
        case 'export_rates':
          aVal = a.export_rates !== null ? Number(a.export_rates) : -Infinity;
          bVal = b.export_rates !== null ? Number(b.export_rates) : -Infinity;
          break;
        case 'inventory':
          const invA = inventoryByResin[resinType]?.[a.date] ?? -Infinity;
          const invB = inventoryByResin[resinType]?.[b.date] ?? -Infinity;
          aVal = invA;
          bVal = invB;
          break;
        default:
          return 0;
      }

      if (direction === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    return sorted;
  };

  const getSortIcon = (resinType: string, column: SortColumn) => {
    const { column: sortCol, direction } = sortState[resinType];
    if (sortCol !== column) {
      return <ArrowUpDown size={14} className="opacity-40" />;
    }
    return direction === 'asc' ?
      <ArrowUp size={14} /> :
      <ArrowDown size={14} />;
  };

  const exportCSV = (resinType: string, rows: PERatesItem[]) => {
    const headers = ['Date', 'Demand Rates (%)', 'Operating Rates (%)', 'Export Rates (%)', 'Days of Inventory'];
    const pct = (v?: number | null) => (v !== null && v !== undefined ? (Number(v) * 100).toFixed(1) : '');
    const dataRows = rows.map((row) => {
      const doi = inventoryByResin[resinType]?.[row.date];
      return [
        row.date,
        pct(row.demand_rates),
        pct(row.operating_rates),
        pct(row.export_rates),
        doi !== null && doi !== undefined ? String(Math.round(Number(doi))) : '',
      ].join(',');
    });
    const csvContent = [headers.join(','), ...dataRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `PE-Rates-${(RESIN_LABELS[resinType] || resinType).replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className={`flex flex-col gap-4 ${isCompact ? 'p-0 -mx-6' : 'p-4'}`}>
      {loading ? (
        <div className="flex items-center justify-center h-[700px] text-rs-off-white/50">
          Loading rates data…
        </div>
      ) : (
        <>
          {/* Line chart — demand rates & operating rates */}
          <div className={`bg-rs-off-black rounded-lg ${isCompact ? 'p-2' : 'p-4'}`}>
            <h3 className={`text-rs-off-white font-semibold mb-3 text-sm ${isCompact ? 'text-center' : ''}`}>Demand Rates & Operating Rates</h3>
            <div className="h-[700px]" data-testid="rates-chart">
              <Line data={chartData} options={chartOptions} plugins={[hoverLinePlugin, areaPlugin]} />
            </div>
            {hasWindowing && (
              <ChartRangeSlider
                totalPoints={chartLabels.length}
                startIndex={startIndex}
                endIndex={endIndex}
                onChange={setWindow}
              />
            )}
          </div>

          {/* 4 data tables in 2x2 grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rates-tables">
            {RESIN_TYPES.map((rt) => {
              const rows = dataByResin[rt]
                .slice()
                .sort((a, b) => b.date.localeCompare(a.date));
              const color = RESIN_COLORS[rt] || RESIN_COLORS.pe;
              return (
                <div key={rt} className="bg-rs-off-black rounded-lg overflow-hidden">
                  {/* centered resin label with export button on the right */}
                  <div className="relative flex justify-center items-center py-3 bg-rs-off-black/80">
                    <div
                      className="px-4 py-1 rounded-md bg-rs-jet text-rs-off-white text-sm font-semibold"
                      style={{
                        backgroundColor: color.header,
                        background: color.bg,
                        color: color.header,
                      }}
                    >
                      {RESIN_LABELS[rt] || rt.toUpperCase()}
                    </div>
                    <button
                      onClick={() => exportCSV(rt, [...rows].sort((a, b) => b.date.localeCompare(a.date)))}
                      disabled={rows.length === 0}
                      className="absolute right-3 flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-rs-green text-black hover:bg-rs-dark-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Export"
                    >
                      <Download size={14} />
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-[420px]">
                    <table className="w-full text-xs" data-testid={`rates-table-${rt}`}>
                      <thead>
                        <tr className="bg-rs-jet text-rs-off-white/70">
                          <th
                            className="px-3 py-2 text-left cursor-pointer hover:bg-rs-jet-light transition-colors"
                            onClick={() => handleColumnSort(rt, 'date')}
                          >
                            <div className="flex items-center gap-1.5">
                              Date
                              {getSortIcon(rt, 'date')}
                            </div>
                          </th>
                          <th
                            className="px-3 py-2 text-right cursor-pointer hover:bg-rs-jet-light transition-colors"
                            onClick={() => handleColumnSort(rt, 'demand_rates')}
                          >
                            <div className="flex items-center justify-end gap-1.5">
                              Demand Rates
                              {getSortIcon(rt, 'demand_rates')}
                            </div>
                          </th>
                          <th
                            className="px-3 py-2 text-right cursor-pointer hover:bg-rs-jet-light transition-colors"
                            onClick={() => handleColumnSort(rt, 'operating_rates')}
                          >
                            <div className="flex items-center justify-end gap-1.5">
                              Operating Rates
                              {getSortIcon(rt, 'operating_rates')}
                            </div>
                          </th>
                          <th
                            className="px-3 py-2 text-right cursor-pointer hover:bg-rs-jet-light transition-colors"
                            onClick={() => handleColumnSort(rt, 'export_rates')}
                          >
                            <div className="flex items-center justify-end gap-1.5">
                              Export Rates
                              {getSortIcon(rt, 'export_rates')}
                            </div>
                          </th>
                          <th
                            className="px-3 py-2 text-right cursor-pointer hover:bg-rs-jet-light transition-colors"
                            onClick={() => handleColumnSort(rt, 'inventory')}
                          >
                            <div className="flex items-center justify-end gap-1.5">
                              Days of Inventory
                              {getSortIcon(rt, 'inventory')}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedRows(rows, rt).map((row, idx) => {
                          const doi = inventoryByResin[rt]?.[row.date];
                          const rowBg = isDark
                            ? (idx % 2 === 0 ? SHADING.rowEven.dark : SHADING.rowOdd.dark)
                            : (idx % 2 === 0 ? SHADING.rowEven.light : SHADING.rowOdd.light);
                          return (
                            <tr key={row.date} className="border-t border-rs-jet-light text-rs-off-white/70" style={{ backgroundColor: rowBg }}>
                              <td className="px-3 py-1.5">{format(new Date(row.date + 'T00:00:00'), isCompact ? 'yyyy-MM-dd' : 'yyyy-MMM-dd')}</td>
                              <td className="px-3 py-1.5 text-right">{formatRate(row.demand_rates)}</td>
                              <td className="px-3 py-1.5 text-right">{formatRate(row.operating_rates)}</td>
                              <td className="px-3 py-1.5 text-right">{formatRate(row.export_rates)}</td>
                              <td className="px-3 py-1.5 text-right">{formatDays(doi)}</td>
                            </tr>
                          );
                        })}
                        {rows.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-center text-rs-highlight-gray">
                              No data
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PERatesTab;
